import { compose, cva } from "cva";
import { Check, ChevronRight } from "lucide-react";
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuProps as AriaMenuProps,
  MenuSection as AriaMenuSection,
  MenuSectionProps as AriaMenuSectionProps,
  Collection,
  composeRenderProps,
  Header,
  MenuItemProps,
  SeparatorProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Popover, PopoverProps } from "./Popover";
import { Separator } from "./Separator";
import { composeTailwindRenderProps, focusRing } from "./utils";

interface MenuProps<T> extends AriaMenuProps<T> {
  placement?: PopoverProps["placement"];
}

export function Menu<T extends object>(props: MenuProps<T>) {
  return (
    <Popover
      placement={props.placement}
      className="colors-glass backdrop-blur-glass min-w-[150px] rounded-md"
    >
      <AriaMenu
        {...props}
        className="colors-glass backdrop-blur-glass max-h-[inherit] overflow-y-auto rounded-md p-3 text-[11px]/[16px]"
      />
    </Popover>
  );
}

export function MenuItem(props: MenuItemProps) {
  return (
    <AriaMenuItem
      {...props}
      className={(renderProps) =>
        twMerge(
          focusRing(renderProps),
          "hover:bg-theme-on-background-hover flex h-11 cursor-pointer items-center gap-x-3 rounded-md px-4 font-medium text-nowrap transition-[background-color] duration-200 disabled:cursor-not-allowed",
          renderProps.selectionMode === "single" &&
            renderProps.isSelected &&
            "text-theme-active",
          composeRenderProps(
            props.className,
            (className) => className,
          )(renderProps),
        )
      }
    >
      {(renderProps) => (
        <>
          <div className="truncate">
            {composeRenderProps(
              props.children,
              (children) => children,
            )(renderProps)}
          </div>
        </>
      )}
    </AriaMenuItem>
  );
}

export function MenuSeparator(props: SeparatorProps) {
  return <Separator {...props} className="mx-3 my-1" />;
}

export interface MenuSectionProps<T> extends AriaMenuSectionProps<T> {
  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items?: any;
}

export function MenuSection<T extends object>(props: MenuSectionProps<T>) {
  return (
    <AriaMenuSection className="after:block after:h-[5px] after:content-[''] first:-mt-[5px]">
      <Header className="sticky -top-[5px] z-10 -mx-1 -mt-px truncate border-y bg-gray-100/60 px-4 py-1 text-sm font-semibold text-gray-500 backdrop-blur-md supports-[-moz-appearance:none]:bg-gray-100 dark:border-y-zinc-700 dark:bg-zinc-700/60 dark:text-zinc-300 [&+*]:mt-1">
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </AriaMenuSection>
  );
}
